package com.flowyride.core.network

import io.ktor.client.HttpClient
import io.ktor.client.engine.HttpClientEngine
import io.ktor.client.plugins.HttpTimeout
import io.ktor.client.plugins.UserAgent
import io.ktor.client.plugins.contentnegotiation.ContentNegotiation
import io.ktor.client.plugins.logging.LogLevel
import io.ktor.client.plugins.logging.Logger
import io.ktor.client.plugins.logging.Logging
import io.ktor.client.plugins.logging.SIMPLE
import io.ktor.serialization.kotlinx.json.json
import kotlinx.serialization.json.Json

/** Lenient JSON shared by every remote API: third-party payloads evolve without notice. */
val NetworkJson: Json = Json {
    ignoreUnknownKeys = true
    isLenient = true
    explicitNulls = false
    coerceInputValues = true
}

data class NetworkConfig(
    /** Sent to public OSM services, which require an identifiable client. */
    val userAgent: String,
    val enableLogging: Boolean = false,
    val requestTimeoutMillis: Long = 30_000,
    /** Max silence while waiting for a response; null keeps the engine default (10 s on OkHttp). */
    val socketTimeoutMillis: Long? = null,
)

fun createHttpClient(
    config: NetworkConfig,
    engine: HttpClientEngine = platformHttpClientEngine(),
): HttpClient = HttpClient(engine) {
    // Engines report HTTP errors themselves: each API has its own error payload.
    expectSuccess = false

    install(ContentNegotiation) {
        json(NetworkJson)
    }
    install(HttpTimeout) {
        requestTimeoutMillis = config.requestTimeoutMillis
        config.socketTimeoutMillis?.let { socketTimeoutMillis = it }
    }
    if (platformAllowsUserAgent) {
        install(UserAgent) {
            agent = config.userAgent
        }
    }
    if (config.enableLogging) {
        install(Logging) {
            logger = Logger.SIMPLE
            level = LogLevel.INFO
        }
    }
}

/** The HTTP engine for the current platform. */
expect fun platformHttpClientEngine(): HttpClientEngine

/**
 * Browsers own the User-Agent header; setting it would also trigger a CORS preflight
 * that some public APIs do not answer.
 */
internal expect val platformAllowsUserAgent: Boolean
